import WhatsAppSipSupervisorAPI from 'dashboard/api/whatsappSip/supervisor';

// Debounce helper — coalesces rapid realtime events into a single dispatch.
const debounceTimers = {};
const debouncedDispatch = (dispatch, action, payload, delay = 500) => {
  const key = payload ? `${action}:${JSON.stringify(payload)}` : action;
  clearTimeout(debounceTimers[key]);
  debounceTimers[key] = setTimeout(() => {
    dispatch(action, payload);
    delete debounceTimers[key];
  }, delay);
};

export const state = {
  dashboard: { queues: [], totals: {} },
  agents: [],
  calls: [],
  metrics: { by_queue: [], by_agent: [], by_hour: [] },
  // Período atualmente exibido na tela de métricas (today/week/month/...).
  // Guardado pra o refetch via realtime respeitar o filtro do supervisor em
  // vez de forçar 'today'.
  metricsPeriod: 'today',
  uiFlags: {
    isFetchingDashboard: false,
    isFetchingAgents: false,
    isFetchingCalls: false,
    isFetchingMetrics: false,
  },
};

export const getters = {
  getDashboard: $state => $state.dashboard,
  getAgents: $state => $state.agents,
  getCalls: $state => $state.calls,
  getMetrics: $state => $state.metrics,
  getUIFlags: $state => $state.uiFlags,
};

export const actions = {
  async fetchDashboard({ commit }) {
    commit('SET_UI_FLAG', { isFetchingDashboard: true });
    try {
      const { data } = await WhatsAppSipSupervisorAPI.getDashboard();
      const dashboard = data.data || data || {};
      commit('SET_DASHBOARD', dashboard);
      return dashboard;
    } finally {
      commit('SET_UI_FLAG', { isFetchingDashboard: false });
    }
  },

  async fetchAgents({ commit }) {
    commit('SET_UI_FLAG', { isFetchingAgents: true });
    try {
      const { data } = await WhatsAppSipSupervisorAPI.getAgents();
      const agents = data.data?.agents || data.agents || [];
      commit('SET_AGENTS', agents);
      return agents;
    } finally {
      commit('SET_UI_FLAG', { isFetchingAgents: false });
    }
  },

  async fetchCalls({ commit }) {
    commit('SET_UI_FLAG', { isFetchingCalls: true });
    try {
      const { data } = await WhatsAppSipSupervisorAPI.getCalls();
      const calls = data.data?.calls || data.calls || [];
      commit('SET_CALLS', calls);
      return calls;
    } finally {
      commit('SET_UI_FLAG', { isFetchingCalls: false });
    }
  },

  async fetchMetrics({ commit }, params = {}) {
    if (params.period) commit('SET_METRICS_PERIOD', params.period);
    commit('SET_UI_FLAG', { isFetchingMetrics: true });
    try {
      const { data } = await WhatsAppSipSupervisorAPI.getMetrics(params);
      const metrics = data.data || data || {};
      commit('SET_METRICS', metrics);
      return metrics;
    } finally {
      commit('SET_UI_FLAG', { isFetchingMetrics: false });
    }
  },

  async startSnoop(_, { channelId, mode, supervisorExtension }) {
    const { data } = await WhatsAppSipSupervisorAPI.snoop({
      channel_id: channelId,
      mode,
      supervisor_extension: supervisorExtension,
    });
    return data.data || data;
  },

  async stopSnoop(_, bridgeId) {
    await WhatsAppSipSupervisorAPI.stopSnoop(bridgeId);
  },

  // Called by ActionCable when an AMI event arrives.
  // Uses debounce to coalesce rapid events into single API calls.
  handleRealtimeEvent({ dispatch, state: $state }, event) {
    const { event_type: eventType } = event;

    switch (eventType) {
      case 'agent_status_changed':
      case 'endpoint_status_changed':
      case 'device_state_changed':
        debouncedDispatch(dispatch, 'fetchAgents');
        debouncedDispatch(dispatch, 'fetchDashboard');
        break;

      case 'caller_joined_queue':
      case 'caller_left_queue':
      case 'agent_answered':
      case 'agent_ring_no_answer':
        debouncedDispatch(dispatch, 'fetchDashboard');
        debouncedDispatch(dispatch, 'fetchCalls');
        break;

      case 'caller_abandoned':
      case 'call_completed':
        debouncedDispatch(dispatch, 'fetchDashboard');
        debouncedDispatch(dispatch, 'fetchCalls');
        debouncedDispatch(dispatch, 'fetchMetrics', {
          period: $state.metricsPeriod,
        });
        break;

      case 'channel_created':
      case 'channel_destroyed':
      case 'bridge_changed':
        debouncedDispatch(dispatch, 'fetchCalls');
        break;

      default:
        break;
    }
  },
};

export const mutations = {
  SET_UI_FLAG($state, data) {
    $state.uiFlags = { ...$state.uiFlags, ...data };
  },

  SET_DASHBOARD($state, data) {
    $state.dashboard = data;
  },

  SET_AGENTS($state, agents) {
    $state.agents = agents;
  },

  SET_CALLS($state, calls) {
    $state.calls = calls;
  },

  SET_METRICS($state, metrics) {
    $state.metrics = metrics;
  },
  SET_METRICS_PERIOD($state, period) {
    $state.metricsPeriod = period;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
